import type { AsyncFlow } from '../../../contract/async-flow';
import { AsyncComputedFlowBase, type AsyncComputedFlowOptions } from '../instance';
import type { AsyncFlowComputation, AsyncFlowComputationContext } from '../computation';
import { FlowAwait } from '../flowAwait';

/**
 * Функция, которая вычисляет значение для AsyncComputedGeneratorFlow.
 *
 * Эта функция получает контекст вычисления, который может использоваться для отслеживания
 * зависимостей, и вызывается всякий раз, когда необходимо пересчитать вычисленное значение.
 *
 * @typeParam T - Тип значения, которое будет вычислено и возвращено
 * @param ctx - Контекст вычисления, используемый для отслеживания зависимостей
 * @returns Генератор, который отдает токены ожидания и конечное вычисленное значение типа T
 */
export type AsyncComputedGeneratorFlowGetter<T> = (
    ctx: AsyncFlowComputationContext
) => Generator<FlowAwait, T, undefined>;

/**
 * Асинхронный вычисляемый поток, который использует функции-генераторы для асинхронных вычислений.
 *
 * @typeParam T - Тип значения, которое вычисляет и испускает этот поток
 */
export class AsyncComputedGeneratorFlow<T> extends AsyncComputedFlowBase<T> implements AsyncFlow<T> {
    /**
     * Функция, которая вычисляет значение этого потока.
     */
    private getter: AsyncComputedGeneratorFlowGetter<T>;

    /**
     * Создает новый экземпляр AsyncComputedGeneratorFlow.
     *
     * @param getter - Функция-генератор, которая вычисляет значение потока
     * @param options - Необязательная конфигурация для этого вычисляемого потока
     */
    public constructor(getter: AsyncComputedGeneratorFlowGetter<T>, options?: AsyncComputedFlowOptions<T>) {
        super(options);
        this.getter = getter;
    }

    /**
     * Выполняет асинхронное вычисление, используя функцию-геттер на основе генератора.
     *
     * @param computation - Контекст вычисления для этой асинхронной операции
     * @returns AsyncFlowComputation, содержащий вычисленное значение или состояние ошибки
     */
    protected computeAsync(computation: AsyncFlowComputation<T>) {
        let iter: Generator<FlowAwait, T, undefined>;

        try {
            // геттер может оказаться не генератором и бросить прямо на вызове — типы такое не ловят
            iter = this.getter(computation.getContext());
        } catch (error) {
            return this.handleComputationError(computation, error);
        }

        return this.run(iter, computation);
    }

    /**
     * Обрабатывает выполнение итератора генератора.
     *
     * Этот метод проходит через генератор, ожидая отданные токены ожидания. Реджект такого
     * ожидания бросается в генератор, на строку с `yield*`, чтобы геттер мог обработать его
     * своим try/catch. Любое другое отданное значение — нарушение контракта драйвера: ошибка
     * проходит мимо генератора и завершает вычисление.
     *
     * @param iterator - Итератор генератора для выполнения
     * @param computation - Контекст вычисления для этой асинхронной операции
     * @param assertPromiseResult - Функция, которая проверяет результат ранее возвращенного промиса.
     * @returns AsyncFlowComputation, содержащий вычисленное значение или состояние ошибки
     */
    private run(
        iterator: Generator<FlowAwait, T, undefined>,
        computation: AsyncFlowComputation<T>,
        assertPromiseResult: (() => void) | null = null
    ): AsyncFlowComputation<T> {
        try {
            let result: IteratorResult<FlowAwait, T> | null = null;
            for (;;) {
                if (result?.done) {
                    computation.setValue(this.getSuccessValue(result.value));
                    this.onComputationFinished(computation);
                    return computation;
                }

                if (result) {
                    // Распаковка намеренно вне try вокруг генератора: отданный не-токен — ошибка
                    // самого геттера, и прикладной try/catch вокруг `yield` не должен ее глушить
                    FlowAwait.unwrap(result.value).then(
                        () => {
                            this.run(iterator, computation);
                        },
                        (err: unknown) => {
                            this.run(iterator, computation, () => {
                                throw err;
                            });
                        }
                    );
                    return computation;
                }

                if (assertPromiseResult) {
                    const assertResult = assertPromiseResult;
                    assertPromiseResult = null;

                    try {
                        assertResult();
                    } catch (err) {
                        result = iterator.throw(err);
                        continue;
                    }
                }

                result = iterator.next();
            }
        } catch (error) {
            return this.handleComputationError(computation, error);
        }
    }
}
